import { Dialog } from "primereact/dialog"
import { AvaliadorPlantioType, ImovelType, PlanoType, PlantioType, STATUS } from "../../db/Orm"
import { Card } from "primereact/card"
import API from "../../api/API"
import { useEffect, useMemo, useRef, useState } from "react"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import * as mapas from "../../utils/mapas"
import L from "leaflet"
import { Button } from "primereact/button"
import { Toolbar } from "primereact/toolbar"
import { BlockUI } from "primereact/blockui"
import { ProgressSpinner } from "primereact/progressspinner"
import { AxiosError } from "axios"
import { Toast } from "primereact/toast"
import { Message } from "primereact/message"

type OpcoesAvaliacaoType = 'aprovar'|'reprovar'

type AvaliacaoPlantioProps = {
    visible: boolean
    hide: ()=>void
    antecipacao: PlanoType
    avaliador: AvaliadorPlantioType
    carregarPlanos: ()=>Promise<void>|(()=>void)
}
const templateIrrigacao = (plantio:PlantioType)=>{
    if (!plantio.irrigacao_fluvial && !plantio.irrigacao_poco) return 'NENHUM'
    if (plantio.irrigacao_fluvial && !plantio.irrigacao_poco) return 'FLUVIAL'
    if (!plantio.irrigacao_fluvial && plantio.irrigacao_poco) return 'POÇO'
    return 'FLUVIAL E POÇO'
}
const templateLotes = (plantio:PlantioType)=>{
    if (!plantio.lotes?.length) return '-'
    return plantio.lotes.map((v)=>v.id).join(', ')
}
const templateConfirmButton = (avaliacao: OpcoesAvaliacaoType, avaliador: AvaliadorPlantioType)=>{
    return `${avaliacao==='aprovar'?'Aprovar':'Reprovar'} como ${avaliador==='rt'?'RT': 'AIBA'}`
}
const templateMessageAvaliado = (antecipacao: PlanoType, avaliador: AvaliadorPlantioType)=>{
    if (avaliador==='rt'){
        if (antecipacao.aprovacao_rt_status===STATUS.APROVADO){
            return <Message severity='success' text='Os plantios deste plano já foram avaliados pelo responsável técnico. Status: Aprovado' />
        }
        if (antecipacao.aprovacao_rt_status===STATUS.REPROVADO){
            return <Message severity='error' text='Os plantios deste plano já foram avaliados pelo responsável técnico. Status: Reprovado' />
        }
    }
    if (avaliador==='aiba'){
        if (antecipacao.aprovacao_aiba_status===STATUS.APROVADO){
            return <Message severity='success' text='Os plantios deste plano já foram avaliados pela AIBA. Status: Aprovado' />
        }
        if (antecipacao.aprovacao_aiba_status===STATUS.REPROVADO){
            return <Message severity='error' text='Os plantios deste plano já foram avaliados pela AIBA. Status: Reprovado' />
        }
    }
    return <Message severity='warn' text='Estado de avaliação inesperado. Se você está vendo esta mensagem, alguma coisa deu errado.' />
}
export const CardAvaliacaoPlantio = (props: AvaliacaoPlantioProps)=>{
    const toast = useRef<Toast>(null)
    const municipios = API.getMunicipiosBahia() || []
    const municipio = useMemo(()=>municipios.find((v)=>v.id===props.antecipacao.propriedade?.municipio)
    , [props.antecipacao.propriedade?.municipio])
    const carregarPropriedades = async(map: L.Map)=>{
        const propriedade: ImovelType|null = props.antecipacao.propriedade
        if (!propriedade) return
        const poligUrl = propriedade.poligono
        if (!poligUrl) return
        const polig = await mapas.carregarZipFromURL(poligUrl, mapas.estiloContorno)
        polig.addTo(map)
        map.flyToBounds(polig.getBounds())
        propriedade.lotes.forEach(async v=>{
            const lotePolig = await mapas.carregarZipFromURL(v.poligono, mapas.estiloArea)
            lotePolig.addTo(map)
            const polCenter = lotePolig.getBounds().getCenter()
            L.marker(polCenter, {
                icon: L.divIcon({
                    html: v.id?.toString(),
                    className: 'loteText'
                }),
                interactive: false
            }).addTo(map)
        })
    }
    const mapRef = useRef<any>(null)
    const lmapRef = useRef<L.Map|null>(null)
    useEffect(()=>{
        if (!props.visible){
            lmapRef.current = null
            setShowConfirm(false)
            return
        }
        if (lmapRef.current) return
        setTimeout(()=>{
            if (!mapRef.current) return
            const map = mapas.createMapOnBahia(mapRef, undefined, false, false, false)
            lmapRef.current = map
            carregarPropriedades(map)
        }, 1)
    }, [props.visible])
    
    const [showConfirm, setShowConfirm] = useState<boolean>(false)
    const [avaliacaoAction, setAvaliacaoAction] = useState<OpcoesAvaliacaoType>('aprovar')
    const [waiting, setWaiting] = useState<boolean>(false)
    const loadConfirmMessage = (avaliacao: OpcoesAvaliacaoType)=>{
        setShowConfirm(true)
        setAvaliacaoAction(avaliacao)
    }
    const avaliarPlantios = async()=>{
        const planoId = props.antecipacao.id
        if (!planoId) return
        const statusAvaliacao = avaliacaoAction === 'aprovar' ? 'a' : 'r'
        setWaiting(true)
        try {
            await API.avaliarPlantiosPlano(planoId, props.avaliador, statusAvaliacao)
            await props.carregarPlanos()
            setShowConfirm(false)
            toast.current?.show({severity: 'success', summary: 'Avaliação realizada', detail: `Os plantios foram ${avaliacaoAction === 'aprovar' ? 'aprovados' : 'reprovados'} com sucesso!`, life: 3000})
        } catch (error) {
            console.error(error)
            if (error instanceof AxiosError){
                toast.current?.show({severity: 'error', summary: 'Erro na avaliação', detail: `Não foi possível avaliar o plantio: ${error.response?.data?.detail || error.message}`, life: 3000})
            } else {
                toast.current?.show({severity: 'error', summary: 'Erro na avaliação', detail: `Erro desconhecido`, life: 3000})
            }
        }
        setWaiting(false)
    }
    const buttonContent = <>
        {(props.avaliador === 'rt' && props.antecipacao.aprovacao_rt_status === STATUS.NOVO) || (props.avaliador === 'aiba' && props.antecipacao.aprovacao_aiba_status === STATUS.NOVO) ? <>
            <Button label='Aprovar' severity="success" onClick={()=>loadConfirmMessage('aprovar')} />
            <Button label='Reprovar' severity="danger" onClick={()=>loadConfirmMessage('reprovar')} />
        </>:templateMessageAvaliado(props.antecipacao, props.avaliador)}
    </>
    const showConfirmTemplate = <Card title={`Confirmar ${avaliacaoAction === 'aprovar' ? 'Aprovação' : 'Reprovação'}`}>
        {!waiting?<>
            <p>Tem certeza que deseja {avaliacaoAction} este plantio?</p>
            <div className='flex-end'>
                <Button label={templateConfirmButton(avaliacaoAction, props.avaliador)} onClick={avaliarPlantios} />
                <Button label='Voltar' severity="secondary" onClick={()=>setShowConfirm(false)} />
            </div>
        </>:<>
        <div style={{display: 'flex'}}>
            <ProgressSpinner/>
        </div>
        </>}
    </Card>
    return <Dialog header="Avaliação de Plantio" visible={props.visible} onHide={props.hide} className='antecipacao-avalplantio' >
        <Toast ref={toast} position='center' />
        <BlockUI blocked={showConfirm} template={showConfirmTemplate}>
            <Card title="Dados dos Plantios" >
                <table>
                    <tr>
                        <th>Nome do Imóvel</th>
                        <td>{props.antecipacao.propriedade?.nome}</td>
                    </tr>
                    <tr>
                        <th>Município</th>
                        <td>{municipio?.nome}</td>
                    </tr>
                    <tr>
                        <th>Período da Safra</th>
                        <td>{props.antecipacao.safra.periodo}</td>
                    </tr>
                    <tr>
                        <th>Responsável Técnico</th>
                        <td>{props.antecipacao.responsavel_tecnico?.pessoa?.nome_razao || 'NÃO INFORMADO'}</td>
                    </tr>
                </table>
                <DataTable value={props.antecipacao.plantios||[]} dataKey='id' >
                    <Column field='data' header='Data de Plantio' />
                    <Column field='cultura' header='Cultura' />
                    <Column field='variedade' header='Variedade' />
                    <Column field='produtividade' header='Produtividade' />
                    <Column header='Irrigação' body={templateIrrigacao} />
                    <Column header='ID dos Lotes' body={templateLotes}/>
                </DataTable>
                <div ref={mapRef} style={{height: '400px'}}/>
            </Card>
            <Toolbar end={buttonContent} />
        </BlockUI>
    </Dialog>
}